import { Download, InfoOutlined } from '@mui/icons-material'
import React, { useEffect, useState } from 'react'
import { useCellOwnerAgreementsGetPdfUrlLazyQuery } from '../../../../api/asset-holders/assetHolderApi'
import { type CellOwnerAgreementUrl } from '../../../../api/schema-dtos/AssetHolderDomainDtos'
import { type AssetHolding } from '../../../../api/schema-dtos/DashboardDomainDtos'
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Box,
  List,
  ListItem,
  ListItemText, ListItemButton, ListItemIcon, Tooltip
} from '@mui/material'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { useAppDispatch } from '../../../../app/hooks'
import { openInNewTab } from '../../../../utility/DomWindowHelpers'
import { setNotification } from '../../../shared/components/ui-widgets/uiWidgetsSlice'
import { RtkQueryHelper } from '../../../../api/shared/RtkQueryHelper'
import { type AssetHolderUpdateDto } from '../../../../api/schema-dtos/AssetHolderCrudDtos'
import ChangeAccumulationCurrencyComponent from './ChangeAccumulationCurrencyComponent'

interface AssetHoldingProps {
  assetHolder: AssetHolderUpdateDto
  assetHolding: AssetHolding
}

const SolarProjectListItem: React.FC<AssetHoldingProps> = ({ assetHolder, assetHolding }: AssetHoldingProps) => {
  const dispatch = useAppDispatch()
  const accordionIdentifier = `dashboard-solar-project-list-item-${assetHolder.assetHolderId}-${assetHolding.project.urlSlug}`

  const mapProjectStatusText = (projectStatus: string): string => {
    switch (projectStatus) {
      case 'CLOSED':
        return 'Installing Solar Cells'
      case 'PRODUCTION':
        return 'Generating Solar Power'
      case 'REFUNDED':
        return 'Refunded'
      case 'PURCHASED_BY_LESSEE':
        return 'Purchased By Lessee'
      default:
        return projectStatus
    }
  }

  const mapProjectStatusDescription = (projectStatus: string, description: string | undefined): string => {
    if (description === undefined || description === '') {
      if (projectStatus === 'PRODUCTION') {
        return 'Operating normally'
      }
      return '-'
    }

    return description ?? ''
  }

  const mapProjectStatusColor = (projectStatus: string): string => {
    switch (projectStatus) {
      case 'PRODUCTION':
        return 'primary'
      default:
        return 'text.secondary'
    }
  }

  // =================================================================================================================
  // Download the COA PDF. Query API to get the Signed S3 URL to the PDF;
  // return the PDF url and trigger an open in new tab.
  // =================================================================================================================

  const [apiGetCoaPdfUrlTrigger] = useCellOwnerAgreementsGetPdfUrlLazyQuery()
  const [signedUrl, setSignedUrl] = useState<string | null>(null)

  const handleDownloadCoaPdf = async (projectId: number): Promise<void> => {
    apiGetCoaPdfUrlTrigger({
      assetHolderId: assetHolder.assetHolderId, projectId
    })
      .unwrap()
      .then((triggerResult) => {
        const url = (triggerResult as CellOwnerAgreementUrl).s3Url
        setSignedUrl(url)
      })
      .catch((err) => {
        dispatch(setNotification({ open: true, severity: 'error', message: RtkQueryHelper.mapResponseError(err) }))
      })
  }

  useEffect(() => {
    if (signedUrl !== null) {
      openInNewTab(signedUrl)
    }
  }, [signedUrl])

  // =================================================================================================================

  const showUsageAndFinanceInfo = (assetHolding.solarProject.isOperating || assetHolding.project.isSettled)

  return (
    <>
      <Accordion sx={{ px: 4, pt: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon color={'primary'}/>}
                          aria-controls={accordionIdentifier}
                          id={accordionIdentifier} sx={{ px: 0 }}>
          <Grid container>
            <Grid item md={2}>
              <Box component="img" src={assetHolding.project.cmsMainImageUrl} sx={{ width: '100%' }}/>
            </Grid>

            <Grid item md={10} sx={{ pl: 2 }}>
              <Grid container>
                <Grid item md={12}>
                  <Typography variant="h5">{assetHolding.project.displayName}</Typography>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="h5" color={'secondary.main'} sx={{ pr: 3, display: 'inline-block' }}>
                      {assetHolding.solarProject.numberOfCells} cells
                    </Typography>

                    {/* <Typography variant="h5" color={'secondary.main'} sx={{ pr: 3, display: 'inline-block' }}> */}
                    {/*  {assetHolding.solarProjectPerformance?.energyGeneratedKWhFormatted ?? '0.00'} kWh */}
                    {/* </Typography> */}

                    {/* <Typography variant="h5" color={'secondary.main'} sx={{ pr: 3, display: 'inline-block' }}> */}
                    {/*  {assetHolding.solarProjectPerformance?.carbonReductionKgFormatted ?? '0.00'} kg CO<sub>2</sub> */}
                    {/* </Typography> */}
                  </Box>

                  <Box sx={{ mt: 2 }}>
                    <Typography variant="body1" color={mapProjectStatusColor(assetHolding.project.projectStatusCode)}>
                      {mapProjectStatusText(assetHolding.project.projectStatusCode).toUpperCase()}
                    </Typography>

                    <Typography variant="body1" color={'text.secondary'}>
                      <b>Status:</b>&nbsp;&nbsp;{
                      mapProjectStatusDescription(
                        assetHolding.project.projectStatusCode,
                        assetHolding.project.projectStatusDescription
                      )}
                    </Typography>
                  </Box>
                </Grid>

                {/* ======================================================================================== */}

              </Grid>
            </Grid>

          </Grid>
        </AccordionSummary>

        <AccordionDetails sx={{ p: 0, m: 0 }}>
          <Grid container>

            {/* ======================================================================================== */}

            <Grid item md={12} xs={12} sx={{ display: 'flex', pb: 2 }} alignItems={'center'}>
              <Typography variant="h4" color={'text.secondary'} sx={{ mr: 2 }}>
                Your cells: earning {assetHolding.distribution.accumulationCurrencyInstructionDisplayCode}
              </Typography>
              <ChangeAccumulationCurrencyComponent
                assetHolderDetails={assetHolder}
                projectId={assetHolding.project.projectId}
                currentCurrencyCode={assetHolding.distribution.accumulationCurrencyInstructionCode}
              />
            </Grid>

            {/* ======================================================================================== */}

            <Grid item md={3} xs={6}>
              <Typography variant="body1" color={'text.secondary'}>Cost of cells</Typography>
              <Typography variant="body2" color={'text.secondary'}>
                {assetHolding.financials.projectCurrencyCode} {assetHolding.financials.totalCostOfCellsFormatted}
              </Typography>
            </Grid>

            {showUsageAndFinanceInfo && (
              <Grid item md={3} xs={6}>
                <Typography variant="body1" color={'text.secondary'}>Earnings to date</Typography>
                {assetHolding.financials.earnings.map((earning) => (
                  <Typography variant="body2" color={'text.secondary'} key={earning.accumulationCurrencyDisplayCode}>
                    {earning.accumulationCurrencyDisplayCode} {earning.totalAccumulatedEarningsFormatted}
                  </Typography>
                ))}
              </Grid>
            )}

            <Grid item md={3} xs={6}>
              <Typography variant="body1" color={'text.secondary'}>Rental distribution</Typography>
              <Box >
                <Typography variant="body2" color={'text.secondary'}>
                  You: {assetHolding.distribution.assetHolderPercentageFormatted}%
                </Typography>
                {assetHolding.distribution.assetHolderPercentage < 100 && (
                  <Typography variant="body2" color={'text.secondary'}>
                    Charity: {assetHolding.distribution.charityBeneficiaryPercentageFormatted}%
                    <Tooltip title={`The charity is ${assetHolding.distribution.charityBeneficiaryName}`} arrow placement='bottom'>
                      <InfoOutlined sx={{ width: '15px', ml: 1, verticalAlign: 'bottom' }} />
                    </Tooltip>
                  </Typography>
                )}
              </Box>
            </Grid>

            {showUsageAndFinanceInfo && (
              <Grid item md={3} xs={6}>
                <Typography variant="body1" color={'text.secondary'}>Operating since</Typography>
                <Typography variant="body2"
                            color={'text.secondary'}>{assetHolding.solarProject.earlyEnergyDate}</Typography>
              </Grid>
            )}

            {/* {!showUsageAndFinanceInfo && ( */}
            {/*  <> */}
            {/*    <Grid item md={3} xs={6}> */}
            {/*      &nbsp; */}
            {/*    </Grid> */}
            {/*    <Grid item md={3} xs={6}> */}
            {/*      &nbsp; */}
            {/*    </Grid> */}
            {/*  </> */}
            {/* )} */}

            {/* ======================================================================================== */}

            {/* {showUsageAndFinanceInfo && ( */}

              {/* <> */}
              {/*  <Grid item md={12} xs={12} sx={{ mt: 3 }}> */}
              {/*    <Typography variant="h4" color={'text.secondary'}>Summary</Typography> */}
              {/*  </Grid> */}

                {/* <Grid item md={3} xs={6}> */}
                {/*  <Typography variant="body1" color={'text.secondary'}>Energy generated</Typography> */}
                {/*  <Typography variant="body2" */}
                {/*              color={'text.secondary'}> + {assetHolding.solarProjectPerformance?.energyGeneratedKWhFormatted} kWh</Typography> */}
                {/* </Grid> */}

                {/* <Grid item md={3} xs={6}> */}
                {/*  <Typography variant="body1" color={'text.secondary'}>Forecast yield to date</Typography> */}
                {/*  <Typography variant="body2" */}
                {/*              color={'text.secondary'}> {assetHolding.solarProjectPerformance?.forecastYieldKWhFormatted} kWh</Typography> */}
                {/* </Grid> */}

                {/* <Grid item md={3} xs={6}> */}
                {/*  <Typography variant="body1" color={'text.secondary'}>Carbon reduced </Typography> */}
                {/*  <Typography variant="body2" */}
                {/*              color={'text.secondary'}> + {assetHolding.solarProjectPerformance?.carbonReductionKgFormatted} CO<sub>2</sub></Typography> */}
                {/* </Grid> */}
                {/* <Grid item md={12} xs={12}> */}
                {/**/}
                {/* </Grid> */}
              {/* </> */}
            {/**/}
            {/* )} */}

            {/* ======================================================================================== */}

            <Grid item md={12} xs={6} sx={{ mt: 3 }}>
              <Typography variant="h4" color={'text.secondary'}>Documents</Typography>
            </Grid>

            <Box>
              <List>
                <ListItem disablePadding>
                  <ListItemButton component="a" href={assetHolding.project.cmsPidUrl} target={'_blank'}
                                  rel="noreferrer">
                    <ListItemIcon>
                      <Download/>
                    </ListItemIcon>
                    <ListItemText primary="Project Information (PDF)"/>
                  </ListItemButton>
                </ListItem>

                {assetHolding.documents.cellOwnerAgreementPdfAvailable && (
                  <ListItem disablePadding>
                    <ListItemButton component="a" rel="noreferrer" onClick={async () => {
                      await handleDownloadCoaPdf(assetHolding.project.projectId)
                    }}>
                      <ListItemIcon>
                        <Download/>
                      </ListItemIcon>
                      <ListItemText primary="Cell Owner Agreement (PDF)"/>
                    </ListItemButton>
                  </ListItem>
                )}
              </List>
            </Box>

            {/* ======================================================================================== */}

          </Grid>
        </AccordionDetails>
      </Accordion>
    </>
  )
}

export default SolarProjectListItem
